import { api } from "./api/client";
import type {
  EstimadoVsEmitido,
  MercadoOc,
  OcDeLicitacion,
  OrdenCompraDetalle,
  OrdenesCompraFiltros,
  OrdenesCompraPage,
  PatronCompra,
  ProveedorOc,
  RiesgoFavorito,
} from "@/types";

function toParams(filtros: OrdenesCompraFiltros = {}): Record<string, string> {
  const params: Record<string, string> = {};
  for (const [k, v] of Object.entries(filtros)) {
    if (v === undefined || v === null || v === "") continue;
    params[k] = String(v);
  }
  return params;
}

export const ordenesCompraService = {
  list: async (filtros: OrdenesCompraFiltros = {}): Promise<OrdenesCompraPage> =>
    (await api.get<OrdenesCompraPage>("/ordenes-compra", { params: toParams(filtros) })).data,

  /** Siglas de canal (`tipoCompra`) realmente presentes en la base, con conteo. */
  facetasTipoCompra: async (): Promise<Array<{ tipoCompra: string; ordenes: number }>> =>
    (
      await api.get<Array<{ tipoCompra: string; ordenes: number }>>(
        "/ordenes-compra/facetas/tipo-compra",
      )
    ).data,


  detalle: async (codigo: string): Promise<OrdenCompraDetalle> =>
    (await api.get<OrdenCompraDetalle>(`/ordenes-compra/${encodeURIComponent(codigo)}`)).data,

  porLicitacion: async (codigo: string): Promise<OcDeLicitacion> =>
    (
      await api.get<OcDeLicitacion>(
        `/ordenes-compra/por-licitacion/${encodeURIComponent(codigo)}`,
      )
    ).data,

  vincular: async (
    codigo: string,
    accion: "confirmar" | "descartar",
    licitacionCodigo?: string | null,
  ): Promise<OrdenCompraDetalle> =>
    (
      await api.patch<OrdenCompraDetalle>(
        `/ordenes-compra/${encodeURIComponent(codigo)}/vinculo`,
        { accion, licitacionCodigo: licitacionCodigo ?? null },
      )
    ).data,

  /** Trae desde Mercado Público la licitación de origen si no está sincronizada. */
  hidratarLicitacion: async (
    codigo: string,
  ): Promise<{ estado: "existente" | "creada" | "no-disponible"; orden: OrdenCompraDetalle }> =>
    (
      await api.post<{ estado: "existente" | "creada" | "no-disponible"; orden: OrdenCompraDetalle }>(
        `/ordenes-compra/${encodeURIComponent(codigo)}/hidratar-licitacion`,
        {},
      )
    ).data,

  // ---------- Analítica (Fase B) ----------

  mercado: async (meses = 12, limite = 8): Promise<MercadoOc> =>
    (
      await api.get<MercadoOc>("/ordenes-compra/analytics/mercado", {
        params: { meses: String(meses), limite: String(limite) },
      })
    ).data,

  estimadoVsEmitido: async (meses = 24, limite = 12): Promise<EstimadoVsEmitido> =>
    (
      await api.get<EstimadoVsEmitido>("/ordenes-compra/analytics/estimado-vs-emitido", {
        params: { meses: String(meses), limite: String(limite) },
      })
    ).data,

  proveedor: async (rut: string, meses = 24): Promise<ProveedorOc> =>
    (
      await api.get<ProveedorOc>(
        `/ordenes-compra/analytics/proveedor/${encodeURIComponent(rut)}`,
        { params: { meses: String(meses) } },
      )
    ).data,

  patronCompra: async (organismo: string, meses = 24): Promise<PatronCompra> =>
    (
      await api.get<PatronCompra>("/ordenes-compra/analytics/organismo", {
        params: { organismo, meses: String(meses) },
      })
    ).data,

  riesgoFavorito: async (organismo: string, meses = 24): Promise<RiesgoFavorito> =>
    (
      await api.get<RiesgoFavorito>("/ordenes-compra/analytics/riesgo-favorito", {
        params: { organismo, meses: String(meses) },
      })
    ).data,
};
